import * as React from 'react';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MovieIcon from '@mui/icons-material/Movie';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import ArchiveIcon from '@mui/icons-material/Archive';
import { canPreviewInBrowser } from './previewUtils';

// Define a file type interface
interface FileType {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  path?: string;
  parentId?: string | null;
  [key: string]: any;
}

/**
 * Format file size in bytes to human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get appropriate file icon based on mime type
 * Simplified to avoid TypeScript errors
 */
export const getFileIcon = (file: Partial<FileType>, size: number = 24): React.ReactNode => {
  const mimeType = file.mimeType || 'application/octet-stream';
  // Type fontSize as 'small' | 'medium' | 'large' | 'inherit'
  const fontSize: 'small' | 'medium' | 'large' | 'inherit' = 
    size >= 48 ? 'large' : size >= 36 ? 'medium' : 'small';

  // Using a properly typed approach with createElement
  if (mimeType.startsWith('image/')) {
    return React.createElement(ImageIcon, { fontSize, color: 'primary' } as any);
  }
  if (mimeType === 'application/pdf') {
    return React.createElement(PictureAsPdfIcon, { fontSize, color: 'error' } as any);
  }
  if (mimeType.startsWith('video/')) {
    return React.createElement(MovieIcon, { fontSize, color: 'secondary' } as any);
  }
  if (mimeType.startsWith('audio/')) {
    return React.createElement(AudioFileIcon, { fontSize, color: 'success' } as any);
  }
  if (mimeType.startsWith('text/')) {
    return React.createElement(DescriptionIcon, { fontSize, color: 'info' } as any);
  }
  if (mimeType.includes('javascript') || mimeType.includes('json') || mimeType.includes('html') || mimeType.includes('css')) {
    return React.createElement(CodeIcon, { fontSize, color: 'warning' } as any);
  }
  if (mimeType.includes('zip') || mimeType.includes('compressed') || mimeType.includes('archive')) {
    return React.createElement(ArchiveIcon, { fontSize, color: 'action' } as any);
  }
  return React.createElement(InsertDriveFileIcon, { fontSize, color: 'disabled' } as any);
};

/**
 * Create a dataTransfer object for drag operations
 */
export const createDragData = (items: { id: string; type: 'file' | 'folder' }[]) => {
  const data = {
    items,
    operation: 'move',
  };
  return JSON.stringify(data);
};

/**
 * Parse drag data to extract file/folder info
 */
export const parseDragData = (dataString: string): { 
  items: { id: string; type: 'file' | 'folder' }[];
  operation: 'move' | 'copy';
} | null => {
  try {
    return JSON.parse(dataString);
  } catch (error) {
    return null;
  }
};

/**
 * Determine if a file is a valid image for thumbnails
 */
export const isImageFile = (file: FileType) => {
  return file.mimeType.startsWith('image/') && 
    !file.mimeType.includes('svg'); // SVGs are images but may need special handling
};

/**
 * Determine if a file can be previewed directly in the browser
 */
export const canPreviewFile = (file: FileType) => {
  const mime =
    file.mimeType ||
    (file as FileType & { mimetype?: string }).mimetype ||
    getMimeTypeFromFilename(file.name);
  return canPreviewInBrowser({ name: file.name, mimeType: mime, mimetype: mime });
};

/**
 * Get MIME type from file extension
 */
export const getMimeTypeFromFilename = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase() || '';
  
  const mimeTypes: Record<string, string> = {
    // Images
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    
    // Documents
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    
    // Text
    txt: 'text/plain',
    html: 'text/html',
    htm: 'text/html',
    css: 'text/css',
    csv: 'text/csv',
    
    // Code
    js: 'application/javascript',
    json: 'application/json',
    ts: 'application/typescript',
    jsx: 'application/javascript',
    tsx: 'application/typescript',
    xml: 'application/xml',
    
    // Archives
    zip: 'application/zip',
    rar: 'application/vnd.rar',
    '7z': 'application/x-7z-compressed',
    tar: 'application/x-tar',
    gz: 'application/gzip',
    
    // Media
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    mp4: 'video/mp4',
    avi: 'video/x-msvideo',
    mov: 'video/quicktime',
    wmv: 'video/x-ms-wmv',
    
    // Other
    md: 'text/markdown',
    yaml: 'application/x-yaml',
    yml: 'application/x-yaml',
  };
  
  return mimeTypes[extension] || 'application/octet-stream';
}; 